<template>
  <BaseModal
    :is-open="isOpen"
    :title="isRequest ? 'Restart Request' : 'Restart Game?'"
    max-width="500px"
    :closable="!isRequest"
    @close="handleCancel"
  >
    <div class="restart-approval-container">
      <!-- Icon -->
      <div class="restart-icon" :class="{ warning: !isRequest }">
        <span class="material-symbols-outlined">{{ isRequest ? 'mail' : 'restart_alt' }}</span>
      </div>

      <!-- Request Message (for non-creator) -->
      <div v-if="isRequest" class="request-info">
        <div class="player-info">
          <img
            v-if="requesterData?.profile_picture"
            :src="`/images/profiles/${requesterData.profile_picture}.png`"
            class="requester-avatar"
            :alt="requesterData.player_name"
          />
          <div v-else class="requester-initials">
            {{ getInitials(requesterData?.player_name || '?') }}
          </div>
          <h3 class="requester-name">{{ requesterData?.player_name }}</h3>
        </div>
        <p class="request-message">wants to restart the game</p>
      </div>

      <!-- Confirmation Message (for creator) -->
      <div v-else class="confirm-message">
        <p class="main-text">Are you sure you want to restart the game?</p>
        <p class="warning-text">
          <span class="material-symbols-outlined">warning</span>
          <span>This will reset all progress and start a new game from the beginning.</span>
        </p>
      </div>

      <!-- Impact Info -->
      <div class="impact-info">
        <h4 class="impact-title">Restarting will:</h4>
        <ul class="impact-list">
          <li>
            <span class="material-symbols-outlined">refresh</span>
            <span>Reset all scores to zero</span>
          </li>
          <li>
            <span class="material-symbols-outlined">card_travel</span>
            <span>Redistribute all cards</span>
          </li>
          <li>
            <span class="material-symbols-outlined">groups</span>
            <span>Keep all current players in their seats</span>
          </li>
          <li>
            <span class="material-symbols-outlined">cancel</span>
            <span>Erase all current round progress</span>
          </li>
        </ul>
      </div>

      <!-- Action Buttons -->
      <div class="restart-actions">
        <button @click="handleCancel" class="btn-cancel">
          <span class="material-symbols-outlined">{{ isRequest ? 'block' : 'close' }}</span>
          <span>{{ isRequest ? 'Deny' : 'Cancel' }}</span>
        </button>
        <button @click="handleConfirm" class="btn-confirm">
          <span class="material-symbols-outlined">{{
            isRequest ? 'check_circle' : 'restart_alt'
          }}</span>
          <span>{{ isRequest ? 'Approve Restart' : 'Restart Game' }}</span>
        </button>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import BaseModal from './BaseModal.vue'

/**
 * RestartApprovalModal Component
 *
 * Two modes:
 * 1. Request mode (non-creator): Shows restart request to creator for approval
 * 2. Confirmation mode (creator): Confirms creator wants to restart game
 *
 * Props:
 * - is_request: Whether this is a request (true) or direct action (false)
 * - requester_name: Name of player requesting restart (if request)
 * - requester_id: ID of requesting player
 * - requester_profile_picture: Requester's avatar
 *
 * Emits:
 * - approve: [] - Restart approved
 * - deny: [] - Restart denied (request mode only)
 * - confirm: [] - Restart confirmed (direct mode)
 * - cancel: [] - Cancelled
 */

interface Props {
  isOpen: boolean
  data: {
    is_request?: boolean
    requester_name?: string
    requester_id?: string
    requester_profile_picture?: string
  } | null
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  data: null,
})

const emit = defineEmits<{
  approve: []
  deny: []
  confirm: []
  cancel: []
  close: []
}>()

/**
 * Whether this is a request or direct action
 */
const isRequest = computed(() => props.data?.is_request || false)

/**
 * Requester data
 */
const requesterData = computed(() => ({
  player_name: props.data?.requester_name || 'A player',
  profile_picture: props.data?.requester_profile_picture,
}))

/**
 * Get initials from name
 */
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

/**
 * Handle confirm/approve
 */
function handleConfirm(): void {
  if (isRequest.value) {
    emit('approve')
  } else {
    emit('confirm')
  }
  emit('close')
}

/**
 * Handle cancel/deny
 */
function handleCancel(): void {
  if (isRequest.value) {
    emit('deny')
  } else {
    emit('cancel')
  }
  emit('close')
}
</script>

<style scoped>
.restart-approval-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 0.5rem;
}

/* Icon */
.restart-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(37, 99, 235, 0.2));
  display: flex;
  align-items: center;
  justify-content: center;
}

.restart-icon.warning {
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(245, 158, 11, 0.2));
  animation: pulse-warning 2s ease-in-out infinite;
}

@keyframes pulse-warning {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.4);
  }
  50% {
    box-shadow: 0 0 0 20px rgba(251, 191, 36, 0);
  }
}

.restart-icon .material-symbols-outlined {
  font-size: 3rem;
  color: #60a5fa;
}

.restart-icon.warning .material-symbols-outlined {
  color: #fbbf24;
}

/* Request Info */
.request-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  text-align: center;
}

.player-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.requester-avatar,
.requester-initials {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 3px solid rgba(232, 197, 71, 0.3);
}

.requester-avatar {
  object-fit: cover;
}

.requester-initials {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e8c547, #d4af37);
  color: #0d1f2d;
  font-size: 1.5rem;
  font-weight: 700;
}

.requester-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
}

.request-message {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
}

/* Confirm Message */
.confirm-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  text-align: center;
}

.main-text {
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  margin: 0;
}

.warning-text {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.875rem 1.125rem;
  background: rgba(251, 191, 36, 0.1);
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: 8px;
  font-size: 0.9375rem;
  color: rgba(251, 191, 36, 0.9);
  line-height: 1.5;
  margin: 0;
  text-align: left;
}

.warning-text .material-symbols-outlined {
  font-size: 1.125rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

/* Impact Info */
.impact-info {
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
}

.impact-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 1rem;
}

.impact-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}

.impact-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
}

.impact-list li .material-symbols-outlined {
  font-size: 1.25rem;
  color: rgba(232, 197, 71, 0.7);
  flex-shrink: 0;
  margin-top: 0.125rem;
}

/* Action Buttons */
.restart-actions {
  display: flex;
  gap: 1rem;
  width: 100%;
  margin-top: 0.5rem;
}

.btn-cancel,
.btn-confirm {
  flex: 1;
  padding: 0.875rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: none;
}

.btn-cancel {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.btn-cancel:hover {
  background: rgba(239, 68, 68, 0.25);
  border-color: rgba(239, 68, 68, 0.5);
  transform: translateY(-2px);
}

.btn-confirm {
  background: linear-gradient(135deg, #e8c547 0%, #d4af37 100%);
  color: #0d1f2d;
  box-shadow: 0 4px 12px rgba(232, 197, 71, 0.3);
}

.btn-confirm:hover {
  background: linear-gradient(135deg, #f0d060 0%, #e0bf47 100%);
  box-shadow: 0 6px 16px rgba(232, 197, 71, 0.4);
  transform: translateY(-2px);
}

.btn-confirm:active,
.btn-cancel:active {
  transform: translateY(0);
}

.btn-cancel .material-symbols-outlined,
.btn-confirm .material-symbols-outlined {
  font-size: 1.25rem;
}

/* Responsive */
@media (max-width: 640px) {
  .restart-icon {
    width: 64px;
    height: 64px;
  }

  .restart-icon .material-symbols-outlined {
    font-size: 2.5rem;
  }

  .restart-actions {
    flex-direction: column;
  }
}
</style>
